import { analyticsEvents } from "@healthos/analytics";
import { apiFetch } from "@healthos/api-client";
import { queryKeys } from "@healthos/client-data";
import {
  type AttentionTier,
  attentionTierFrom,
  attentionTierLabel,
  BROADCAST_EVENT_KIND,
  notificationWebTarget,
} from "@healthos/shared";
import { BrandMark, Button } from "@healthos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { type AttentionEvent, useAttentionEvents } from "../hooks/use-attention-events";
import { holdAttentionToasts, useAttentionToastGated } from "../hooks/use-attention-toast-gate";
import { useDayZeroRunActive, useDayZeroTour } from "../hooks/use-day-zero-tour";
import { analytics } from "../lib/analytics";
import { playNotificationChime, primeNotificationChime } from "../lib/notification-sound";
import { useBoot } from "./boot/boot-context";
import { DayZeroFocus } from "./day-zero-focus";
import "./in-app-attention-toast.css";

const AUTO_DISMISS_MS = 120_000;
const FADE_OUT_MS = 200;
const MAX_VISIBLE_TOASTS = 4;
const SEEN_STORAGE_KEY = "healthos_attention_toast_seen_ids";
const MAX_STORED_SEEN_IDS = 100;

export function InAppAttentionToast() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const boot = useBoot();
  const { data } = useAttentionEvents();
  const gated = useAttentionToastGated();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readSeenToastIds());
  const [leavingIds, setLeavingIds] = useState<Set<string>>(() => new Set());

  const isOnToday = location.pathname === "/" || location.pathname === "/today";
  // The tour only speaks off Today; on Today the attention card owns the hop.
  // Enabled on Today too, so the final "back to Today" stop can complete on
  // arrival. The toast itself stays hidden there by the filter below.
  const dayZero = useDayZeroTour();
  // The first run owns the screen until it is finished, coach marks included.
  // A brief-ready toast landing mid-run is a second thing asking for attention
  // while the tutorial is still pointing at the first.
  const dayZeroRunActive = useDayZeroRunActive();
  const stop = dayZero.started ? dayZero.stop : null;
  const tourEvent: AttentionEvent | null = useMemo(
    () =>
      stop
        ? {
            id: stop.key,
            kind: "tour",
            title: stop.title,
            body: stop.body,
            deepLink: stop.route,
            data: { kind: "tour", action: stop.action },
          }
        : null,
    [stop],
  );
  // Memoized: the effects below take this list as a dependency, so rebuilding
  // it every render would re-run them every render.
  const events = useMemo(
    () => [...(tourEvent ? [tourEvent] : []), ...(data?.events ?? [])],
    [tourEvent, data?.events],
  );

  const visibleEvents = events
    .toReversed()
    // A tour stop opts out of the seen-id suppression: its lifecycle lives
    // server-side, so it must keep offering until the member actually takes it
    // rather than vanishing after one render and stalling the run.
    .filter(
      (e) =>
        // A broadcast opens as its own modal, never as a toast.
        e.kind !== BROADCAST_EVENT_KIND &&
        (isTourEvent(e) || !dismissedIds.has(e.id)) &&
        (!isOnToday || isBriefReadyEvent(e)) &&
        // Its own stops still show; everything else waits.
        (isTourEvent(e) || !dayZeroRunActive),
    )
    .slice(0, MAX_VISIBLE_TOASTS);

  const hasBriefReady = visibleEvents.some(isBriefReadyEvent);

  // Memoized by hand: biome's exhaustive-deps does not model the React
  // Compiler, and these feed effect dep arrays below.
  const acknowledgeEventId = useCallback(
    (eventId: string) => {
      setDismissedIds((prev) => storeSeenToastId(prev, eventId));
      void apiFetch(`/api/attention/${encodeURIComponent(eventId)}/consume`, {
        method: "POST",
      })
        .then(() => queryClient.invalidateQueries({ queryKey: queryKeys.ambient }))
        .catch((err) => {
          console.warn("[attention] consume failed:", err);
        });
    },
    [queryClient],
  );

  useEffect(() => {
    if (boot.visible) return;
    for (const e of visibleEvents) if (!isTourEvent(e)) rememberSeenToastId(e.id);
  }, [visibleEvents, boot.visible]);

  // A handoff rendered as a toast counts as shown just as the Today card does,
  // or the funnel loses every stop after the first.
  const shownTourIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (boot.visible) return;
    for (const e of visibleEvents) {
      if (!isTourEvent(e) || shownTourIds.current.has(e.id)) continue;
      shownTourIds.current.add(e.id);
      analytics.track(analyticsEvents.dayZeroStopShown, { stop: e.id });
    }
  }, [visibleEvents, boot.visible]);

  useEffect(() => {
    if (boot.visible) return;
    if (!isOnToday || events.length === 0) return;
    setDismissedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const e of events) {
        if (!isBriefReadyEvent(e) && !next.has(e.id)) {
          next.add(e.id);
          rememberSeenToastId(e.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [boot.visible, isOnToday, events]);

  useEffect(() => {
    if (!hasBriefReady) return;
    setDismissedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const e of events) {
        if (!isBriefReadyEvent(e) && !next.has(e.id)) {
          next.add(e.id);
          rememberSeenToastId(e.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.ambient });
  }, [hasBriefReady, events, queryClient]);

  useEffect(() => {
    primeNotificationChime();
  }, []);

  const chimedIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (boot.visible) return;
    if (!chimedIdsRef.current) chimedIdsRef.current = new Set();
    const chimedIds = chimedIdsRef.current;
    const fresh = visibleEvents.filter((e) => !chimedIds.has(e.id));
    if (fresh.length === 0) return;
    for (const e of fresh) chimedIds.add(e.id);
    playNotificationChime();
  }, [visibleEvents, boot.visible]);

  const markLeaving = useCallback((id: string) => {
    setLeavingIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    const timers: number[] = [];
    for (const event of visibleEvents) {
      if (leavingIds.has(event.id)) continue;
      // A handoff is the current step of a bounded flow, not an interruption to
      // time out. Letting it lapse silently strands the member mid-run.
      if (isTourEvent(event)) continue;
      const eventId = event.id;
      timers.push(window.setTimeout(() => markLeaving(eventId), AUTO_DISMISS_MS));
    }
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [visibleEvents, leavingIds, markLeaving]);

  useEffect(() => {
    if (leavingIds.size === 0) return;
    const ids = Array.from(leavingIds);
    const t = window.setTimeout(() => {
      for (const id of ids) acknowledgeEventId(id);
      setLeavingIds(new Set());
    }, FADE_OUT_MS);
    return () => window.clearTimeout(t);
  }, [leavingIds, acknowledgeEventId]);

  if (boot.visible || gated || visibleEvents.length === 0) return null;

  const open = (event: AttentionEvent) => {
    // Consume only the brief nudge; a non-brief card must stay on Today.
    if (isBriefReadyEvent(event)) acknowledgeEventId(event.id);
    // A Day Zero handoff advances the tour cursor server-side, otherwise the
    // same stop is served again the moment the next screen loads.
    if (dayZero.stop && event.id === dayZero.stop.key) {
      analytics.track(analyticsEvents.dayZeroStopActed, { stop: event.id, outcome: "opened" });
      // Ahead of the navigation, for the same reason the Today card does it:
      // the gate is polled, so the destination's own hold arrives too late.
      holdAttentionToasts(20_000);
      void dayZero.advance(event.id);
    }
    navigate(targetForEvent(event));
  };

  const tourToastUp = visibleEvents.some(isTourEvent);

  // "Not now" promised a return the run has no moment to honour, and because a
  // handoff is exempt from the seen-id suppression it came straight back. It
  // means what Skip means on the Today card: the tutorial is over.
  const declineTour = (event: AttentionEvent) => {
    analytics.track(analyticsEvents.dayZeroSkipped, { atStop: event.id });
    void dayZero.skip();
    markLeaving(event.id);
  };

  return (
    <>
      <DayZeroFocus active={tourToastUp} scrim />
      <div className="attention-toast-stack" aria-live="polite">
        {visibleEvents.map((event, index) => (
          <Toast
            key={event.id}
            event={event}
            leaving={leavingIds.has(event.id)}
            stackIndex={index}
            onOpen={() => open(event)}
            onDismiss={() => (isTourEvent(event) ? declineTour(event) : markLeaving(event.id))}
          />
        ))}
      </div>
    </>
  );
}

function Toast({
  event,
  leaving,
  stackIndex,
  onOpen,
  onDismiss,
}: {
  event: AttentionEvent;
  leaving: boolean;
  stackIndex: number;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const tier = tierFromEvent(event);
  return (
    <div
      className={`attention-toast-wrap attention-toast-wrap--${tier} attention-splash${leaving ? " attention-toast-wrap--leaving" : ""}`}
      style={{ "--stack-index": stackIndex } as React.CSSProperties}
    >
      <aside className="attention-toast" role="dialog" aria-label="Attention card">
        <header className="attention-toast-head">
          <div className="attention-toast-brand">
            <BrandMark size="nav" />
            <span>HealthOS</span>
          </div>
          <span className="attention-toast-stamp">{formatStamp(new Date())}</span>
        </header>

        <div className="attention-toast-body">
          <p className="attention-toast-eyebrow">
            <span className="attention-toast-dot" aria-hidden="true" />
            {formatEyebrow(event)}
          </p>
          <h2 className="attention-toast-title">{event.title ?? "Something to look at"}</h2>
          {event.body ? <p className="attention-toast-text">{event.body}</p> : null}

          <div className="attention-toast-actions">
            <Button
              type="button"
              variant="unstyled"
              className="attention-toast-btn attention-toast-btn--primary"
              onClick={onOpen}
            >
              {openLabelForEvent(event)}
            </Button>
            <Button
              type="button"
              variant="unstyled"
              className="attention-toast-btn attention-toast-btn--ghost"
              onClick={onDismiss}
            >
              {isTourEvent(event) ? "Skip" : "Not now"}
            </Button>
          </div>
        </div>

        <footer className="attention-toast-foot">
          <span>{isTourEvent(event) ? "Part of your first day" : "Tap Open to review"}</span>
          {isTourEvent(event) ? null : <span>{formatTier(event)}</span>}
        </footer>
      </aside>
    </div>
  );
}

function formatStamp(d: Date): string {
  const day = d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} · ${time}`;
}

function formatEyebrow(event: AttentionEvent): string {
  // A tour stop has no urgency to report, and the button already carries the
  // verb, so the eyebrow names where they are being taken.
  if (event.kind === "tour") return "Getting started";
  const tier = formatTier(event);
  const action = formatAction(event.data?.action);
  if (tier && action) return `${tier} · ${action}`;
  return tier || action || "Attention";
}

function formatTier(event: AttentionEvent): string {
  return attentionTierLabel(tierFromEvent(event));
}

// A tour stop names its own destination (for example, "Open Explore"); everything else
// keeps the generic label.
function isTourEvent(event: AttentionEvent): boolean {
  return event.kind === "tour";
}

function openLabelForEvent(event: AttentionEvent): string {
  if (event.kind === "tour" && event.data?.action) return event.data.action;
  return isBriefReadyEvent(event) ? "Open Brief" : "Open Today";
}

function tierFromEvent(event: AttentionEvent): AttentionTier {
  return attentionTierFrom(event.data?.tier) ?? "routine";
}

function formatAction(action: string | undefined): string | null {
  if (action === "microstep.request") return "Microstep";
  if (action === "plan.surface_item") return "Plan item";
  if (action === "reminder.send") return "Reminder";
  if (action === "brief.ready") return "Brief";
  // Anything else is an internal id such as rule.inject_recovery; the tier alone reads better.
  return null;
}

function readSeenToastIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function rememberSeenToastId(id: string): void {
  storeSeenToastId(readSeenToastIds(), id);
}

function storeSeenToastId(current: Set<string>, id: string): Set<string> {
  const next = new Set([...current, id]);
  try {
    const ids = [...next].slice(-MAX_STORED_SEEN_IDS);
    window.localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(ids));
    return new Set(ids);
  } catch {
    return next;
  }
}

function isBriefReadyEvent(event: AttentionEvent): boolean {
  return (
    event.kind === "brief.ready" ||
    event.deepLink === "thrive://brief/today" ||
    event.data?.kind === "brief.ready" ||
    event.data?.action === "brief.ready"
  );
}

function targetForEvent(event: AttentionEvent): string {
  return (
    notificationWebTarget({
      data: event.data,
      deepLink: event.deepLink,
      fallback: isBriefReadyEvent(event) ? "/today?brief=today" : "/today",
      origin: window.location.origin,
      title: event.title,
    }) ?? "/today"
  );
}
